import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowUp, Bell, Flag, Home, MessageSquare, Plus, Search, User, X, ImagePlus, Tag, Sun } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { TorriLogo, TorriMark } from "@/components/TorriLogo";
import { VestaWorldLogo } from "./VestaWorldLogo";
import { B, TIPI } from "./bacheca/common";
import ComposerForm from "./bacheca/Composer";
import PostCard, { Avatar, PostSkeleton } from "./bacheca/PostCard";
import Comments from "./bacheca/Comments";
import Messages from "./bacheca/Messages";
import ShareDialog from "./bacheca/ShareDialog";
import Notifications from "./bacheca/Notifications";

const STYLE = "@keyframes coinBurst{0%{opacity:0;transform:translate(-50%,-50%) scale(.4) rotate(-20deg)}35%{opacity:1;transform:translate(-50%,-50%) scale(1.3) rotate(10deg)}70%{opacity:1;transform:translate(-50%,-60%) scale(1)}100%{opacity:0;transform:translate(-50%,-90%) scale(.9)}}"
  + "@keyframes feedIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}";
const iconBtn = "relative rounded-full p-2 text-[#E5E7EB] transition-colors hover:bg-[#1F242D]";
const Dot = ({ n, testid }) => n > 0 ? <span className="absolute -right-0.5 -top-0.5 min-w-[18px] rounded-full bg-[#C17F59] px-1 text-center text-[10px] font-bold leading-[18px] text-white" data-testid={testid}>{n > 99 ? "99+" : n}</span> : null;

function FullScreen({ children, onClose, sheet }) {
  const [box, setBox] = useState(null);
  useEffect(() => {
    const vv = window.visualViewport;
    const fit = () => { if (vv) setBox({ top: vv.offsetTop, height: vv.height }); window.scrollTo(0, window.scrollY); };
    fit();
    vv?.addEventListener("resize", fit); vv?.addEventListener("scroll", fit);
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    const prev = document.body.style.overflow; document.body.style.overflow = "hidden";
    return () => { vv?.removeEventListener("resize", fit); vv?.removeEventListener("scroll", fit); window.removeEventListener("keydown", esc); document.body.style.overflow = prev; };
  }, [onClose]);
  return (
    <div className={`fixed inset-x-0 z-[60] flex justify-center bg-black/70 ${sheet ? "items-end" : ""}`} style={box ? { top: box.top, height: box.height } : { top: 0, bottom: 0 }}
      onClick={(e) => e.target === e.currentTarget && onClose()} data-testid={sheet ? undefined : "bacheca-composer-sheet"}>
      <div className={`w-full max-w-[600px] overflow-hidden bg-[#16191E] text-white [animation:feedIn_.2s_ease-out] ${sheet ? "h-[75%] rounded-t-2xl border-t border-[#2A303C] sm:mb-6 sm:rounded-2xl sm:border" : "h-full sm:mt-6 sm:h-[calc(100%-3rem)] sm:rounded-2xl sm:border sm:border-[#2A303C]"}`}>{children}</div>
    </div>
  );
}

function TopBar({ unread, notif, onNotif, onSearch, onDm, searchOpen, q, setQ, regione, setRegione, regioni }) {
  return (
    <header className="sticky top-0 z-40 border-b border-[#2A303C] bg-[#0D0F12]/90 backdrop-blur-xl" data-testid="bacheca-topbar">
      <div className="mx-auto flex h-14 max-w-[600px] items-center justify-between px-3">
        <div className="flex min-w-0 items-center gap-2">
          <Link to="/vesta/home" state={{ back: true }} className={iconBtn} data-testid="bacheca-back" title="Tutti i servizi"><ArrowLeft className="h-5 w-5" /></Link>
          <TorriMark className="h-8 w-8 shrink-0 sm:hidden" />
          <TorriLogo className="hidden shrink-0 sm:flex" imgClass="h-8" />
          <span className="mx-1 h-6 w-px shrink-0 bg-[#2A303C] sm:mx-2" />
          <VestaWorldLogo className="h-8 w-8 shrink-0" bg="#0D0F12" />
          <span className="truncate font-heading text-lg font-bold tracking-tight" data-testid="bacheca-title">Vesta World</span>
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={onSearch} className={iconBtn} data-testid="bacheca-search-open"><Search className="h-5 w-5" /></button>
          <button type="button" onClick={onNotif} className={`${iconBtn} hidden sm:block`} data-testid="bacheca-notif-open"><Bell className="h-5 w-5" /><Dot n={notif} testid="bacheca-notif-unread-top" /></button>
          <button type="button" onClick={onDm} className={iconBtn} data-testid="bacheca-dm-open"><MessageSquare className="h-5 w-5" /><Dot n={unread} testid="bacheca-dm-unread" /></button>
        </div>
      </div>
      {searchOpen && (
        <div className="mx-auto flex max-w-[600px] gap-2 px-3 pb-3 [animation:feedIn_.2s_ease-out]" data-testid="bacheca-search-panel">
          <div className="flex flex-1 items-center gap-2 rounded-full border border-[#2A303C] bg-[#121418] px-3">
            <Search className="h-4 w-4 text-[#6B7280]" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca post, impianti, annunci…" className="h-9 w-full bg-transparent text-sm text-white outline-none" data-testid="bacheca-search" />
            {q && <button type="button" onClick={() => setQ("")}><X className="h-4 w-4 text-[#6B7280]" /></button>}
          </div>
          <select value={regione} onChange={(e) => setRegione(e.target.value)} className="h-9 max-w-[40%] rounded-full border border-[#2A303C] bg-[#121418] px-3 text-sm text-white outline-none" data-testid="bacheca-filter-regione">
            <option value="">Tutta Italia</option>
            {regioni.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      )}
    </header>
  );
}

function Chips({ tipo, setTipo, staff, segn, setSegn }) {
  const chip = (on, extra = {}) => ({ className: "shrink-0 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-semibold transition-[background-color,border-color,color] duration-200",
    style: on ? { background: "#C17F59", borderColor: "#C17F59", color: "#fff", boxShadow: "0 2px 10px rgba(193,127,89,.35)", ...extra } : { background: "#16191E", borderColor: "#2A303C", color: "#D1D5DB" } });
  return (
    <div className="sticky top-14 z-30 border-b border-[#2A303C] bg-[#0D0F12]/90 backdrop-blur-xl" data-testid="bacheca-filters">
      <div className="mx-auto flex max-w-[600px] gap-2 overflow-x-auto px-3 py-2.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {[["", "Tutti"], ...Object.entries(TIPI).map(([k, t]) => [k, t.label])].map(([k, l]) => (
          <button key={k || "all"} type="button" onClick={() => { setSegn(false); setTipo(k); }} {...chip(!segn && tipo === k)} data-testid={`bacheca-filter-${k || "tutti"}`}>{l}</button>
        ))}
        {staff && <button type="button" onClick={() => setSegn(!segn)} {...chip(segn, { background: "#f59e0b", borderColor: "#f59e0b", color: "#000" })} data-testid="bacheca-filter-segnalazioni"><Flag className="mr-1 inline h-3 w-3" />Segnalazioni</button>}
      </div>
    </div>
  );
}

function ComposerRow({ me, onOpen }) {
  const quick = [["impianto", "Impianto", Sun, "#34D399"], ["mercatino", "Vendi", Tag, "#38BDF8"], ["discussione", "Foto", ImagePlus, "#E59B6E"]];
  return (
    <div className="border-y border-[#2A303C] bg-[#16191E] px-4 py-3 sm:rounded-2xl sm:border" data-testid="bacheca-composer-row">
      <div className="flex items-center gap-3">
        <Avatar a={me} logo={me?.logo} foto={me?.foto} testid="bacheca-composer-avatar" />
        <button type="button" onClick={() => onOpen("discussione")} className="h-10 flex-1 rounded-full border border-[#2A303C] bg-[#121418] px-4 text-left text-[15px] text-[#9CA3AF] transition-[border-color] hover:border-[#C17F59]" data-testid="bacheca-composer-open">
          A cosa stai lavorando?
        </button>
      </div>
      <div className="mt-2.5 flex justify-around border-t border-[#2A303C] pt-2">
        {quick.map(([k, l, I, c]) => (
          <button key={k} type="button" onClick={() => onOpen(k)} className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold text-[#D1D5DB] hover:bg-[#1F242D]" data-testid={`bacheca-quick-${k}`}><I className="h-4 w-4" style={{ color: c }} />{l}</button>
        ))}
      </div>
    </div>
  );
}

function BottomNav({ notif, onFeed, onSearch, onNew, onNotif, onProfile }) {
  const it = "flex flex-1 flex-col items-center gap-0.5 py-1.5 text-[10px] font-semibold text-[#9CA3AF] transition-colors hover:text-white";
  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-[#2A303C] bg-[#0D0F12]/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl sm:hidden" data-testid="bacheca-bottomnav">
      <div className="flex h-[60px] items-center">
        <button type="button" onClick={onFeed} className={`${it} text-white`} data-testid="bacheca-nav-feed"><Home className="h-6 w-6" />Feed</button>
        <button type="button" onClick={onSearch} className={it} data-testid="bacheca-nav-cerca"><Search className="h-6 w-6" />Cerca</button>
        <button type="button" onClick={onNew} className="flex flex-1 justify-center" data-testid="bacheca-nav-pubblica">
          <span className="-translate-y-3 rounded-full bg-[#C17F59] p-3.5 text-white shadow-lg shadow-[#C17F59]/40 transition-transform active:scale-95"><Plus className="h-6 w-6" /></span>
        </button>
        <button type="button" onClick={onNotif} className={`${it} relative`} data-testid="bacheca-nav-notifiche"><span className="relative"><Bell className="h-6 w-6" /><Dot n={notif} testid="bacheca-notif-unread" /></span>Notifiche</button>
        <button type="button" onClick={onProfile} className={it} data-testid="bacheca-nav-profilo"><User className="h-6 w-6" />Profilo</button>
      </div>
    </nav>
  );
}

export default function VestaBacheca() {
  const [sp, setSp] = useSearchParams();
  const [tipo, setTipo] = useState("");
  const [regione, setRegione] = useState("");
  const [q, setQ] = useState("");
  const [segn, setSegn] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [data, setData] = useState(null);
  const [more, setMore] = useState(false);
  const nav = useNavigate();
  const setAuthor = useCallback((uid) => uid && nav(`/vesta/bacheca/profilo/${uid}`), [nav]);
  const [dmOpen, setDmOpen] = useState(false);
  const [cid, setCid] = useState(null);
  const [unread, setUnread] = useState({ dm: 0, posts: 0, notif: 0 });
  const [notifOpen, setNotifOpen] = useState(false);
  const [composer, setComposer] = useState(null);
  const [comments, setComments] = useState(null);
  const [share, setShare] = useState(null);
  const [focus, setFocus] = useState(null);
  const sentinel = useRef(null);
  const [reload, setReload] = useState(0);
  const params = useCallback((before) => ({ tipo: tipo || undefined, regione: regione || undefined, q: q || undefined, before }), [tipo, regione, q]);
  useEffect(() => {
    const t = setTimeout(() => {
      setData((x) => (x ? { ...x, posts: null } : null));
      const req = segn ? api.get(`${B}/segnalazioni`).then((r) => ({ posts: r.data, has_more: false })) : api.get(`${B}/posts`, { params: params() }).then((r) => r.data);
      req.then((d) => { setData((x) => ({ ...(x || {}), ...d, me: d.me || x?.me })); setUnread((u) => ({ ...u, posts: 0 })); })
        .catch((e) => toast.error(e.response?.data?.detail || "Errore di caricamento"));
    }, 250);
    return () => clearTimeout(t);
  }, [params, segn, reload]);
  useEffect(() => {
    const dm = sp.get("dm");
    if (dm) { setSp({}, { replace: true }); startDm(dm); return; }
    const pid = sp.get("post");
    if (!pid) { setFocus(null); return; }
    api.get(`${B}/posts/${pid}`).then((r) => setFocus(r.data)).catch(() => toast.error("Post non disponibile"));
  }, [sp]);
  useEffect(() => {
    const load = () => api.get(`${B}/unread`).then((r) => setUnread(r.data)).catch(() => {});
    const id = setInterval(load, 20000); load();
    return () => clearInterval(id);
  }, [dmOpen, notifOpen]);
  const loadMore = useCallback(async () => {
    if (more || !data?.has_more || !data.posts?.length) return;
    setMore(true);
    try { const r = await api.get(`${B}/posts`, { params: params(data.posts[data.posts.length - 1].created_at) }); setData((d) => ({ ...d, posts: [...d.posts, ...r.data.posts], has_more: r.data.has_more })); } catch { toast.error("Errore di caricamento"); }
    setMore(false);
  }, [more, data, params]);
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const ob = new IntersectionObserver((e) => e[0].isIntersecting && loadMore(), { rootMargin: "600px" });
    ob.observe(el);
    return () => ob.disconnect();
  }, [loadMore]);
  const upd = (p) => { setData((d) => ({ ...d, posts: d.posts.map((x) => (x.id === p.id ? p : x)) })); setFocus((f) => (f?.id === p.id ? p : f)); };
  const del = (id) => { setData((d) => ({ ...d, posts: d.posts.filter((x) => x.id !== id) })); if (focus?.id === id) setSp({}); };
  const startDm = async (uid) => {
    try { const r = await api.post(`${B}/dm/start`, { uid }); setCid(r.data.id); setDmOpen(true); } catch (e) { toast.error(e.response?.data?.detail || "Impossibile aprire la chat"); }
  };
  const me = data?.me;
  const closeComposer = useCallback(() => setComposer(null), []);
  const closeComments = useCallback(() => setComments(null), []);
  const markRead = useCallback(() => setUnread((u) => ({ ...u, notif: 0 })), []);
  const top = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const fresh = () => { top(); setReload((r) => r + 1); };
  const cardProps = { me, onChange: upd, onDelete: del, onAuthor: setAuthor, onComments: setComments, onShare: setShare };
  return (
    <div className="min-h-screen bg-[#0D0F12] pb-24 text-white sm:pb-10" data-testid="vesta-bacheca-page">
      <style>{STYLE}</style>
      <TopBar unread={unread.dm} notif={unread.notif} onNotif={() => setNotifOpen(true)} onSearch={() => setSearchOpen(!searchOpen)} onDm={() => { setCid(null); setDmOpen(true); }} searchOpen={searchOpen}
        q={q} setQ={setQ} regione={regione} setRegione={setRegione} regioni={data?.regioni || []} />
      <Chips tipo={tipo} setTipo={setTipo} staff={me?.mod} segn={segn} setSegn={setSegn} />
      {unread.posts > 0 && (
        <button type="button" onClick={fresh} className="fixed left-1/2 top-[118px] z-30 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-[#C17F59] px-4 py-2 text-xs font-bold text-white shadow-lg shadow-black/50 [animation:feedIn_.3s_ease-out]" data-testid="bacheca-new-posts">
          <ArrowUp className="h-3.5 w-3.5" />{unread.posts} {unread.posts === 1 ? "nuovo post" : "nuovi post"}
        </button>
      )}
      <main className="mx-auto max-w-[600px] space-y-2 sm:space-y-4 sm:px-0 sm:pt-4">
        {focus && (
          <div className="space-y-1" data-testid="bacheca-focus">
            <div className="flex items-center justify-between px-4 pt-2 text-xs text-[#9CA3AF]"><span>Post condiviso con te</span><button type="button" onClick={() => setSp({})} className="font-semibold hover:text-white" data-testid="bacheca-focus-close">Chiudi</button></div>
            <PostCard p={focus} {...cardProps} />
          </div>
        )}
        {me && <ComposerRow me={me} onOpen={setComposer} />}
        {!data?.posts || !me ? [0, 1].map((i) => <PostSkeleton key={i} />) : (
          <div className="space-y-2 sm:space-y-4" data-testid="bacheca-feed">
            {data.posts.map((p) => <PostCard key={p.id} p={p} {...cardProps} />)}
            {data.posts.length === 0 && <p className="py-12 text-center text-sm text-[#6B7280]" data-testid="bacheca-empty">Ancora nessun post. Inizia tu!</p>}
            <div ref={sentinel} className="h-1" />
            {more && <PostSkeleton />}
            {!data.has_more && data.posts.length > 3 && <p className="py-6 text-center text-xs text-[#6B7280]" data-testid="bacheca-end">Sei in pari con la rete</p>}
          </div>
        )}
      </main>
      <BottomNav notif={unread.notif} onFeed={fresh} onSearch={() => { top(); setSearchOpen(true); }} onNew={() => setComposer("discussione")}
        onNotif={() => setNotifOpen(true)} onProfile={() => me && setAuthor(me.uid)} />
      {composer && (
        <FullScreen onClose={closeComposer}>
          <ComposerForm key={composer} me={me} initialTipo={composer} onCancel={() => setComposer(null)}
            onCreated={(p) => { setData((d) => ({ ...d, posts: [p, ...(d?.posts || [])] })); setComposer(null); top(); }} />
        </FullScreen>
      )}
      {comments && (
        <FullScreen onClose={closeComments} sheet>
          <div className="flex h-full flex-col" data-testid="bacheca-comments-sheet">
            <div className="flex items-center justify-between border-b border-[#2A303C] px-4 py-3">
              <span className="font-heading text-base font-bold">Commenti</span>
              <button type="button" onClick={closeComments} className="rounded-full p-1.5 text-[#9CA3AF] hover:text-white" data-testid="bacheca-comments-close"><X className="h-5 w-5" /></button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-4">
              <Comments p={comments} onAuthor={(u) => { setComments(null); setAuthor(u); }}
                onCount={(n, list) => upd({ ...comments, comments_count: n, comments_preview: (list || []).slice(-2) })} />
            </div>
          </div>
        </FullScreen>
      )}
      <Messages open={dmOpen} onOpenChange={setDmOpen} cid={cid} setCid={setCid} />
      <ShareDialog post={share} onClose={() => setShare(null)} />
      <Notifications open={notifOpen} onOpenChange={setNotifOpen} onRead={markRead}
        onOpenPost={(id) => { setNotifOpen(false); setSp({ post: id }); top(); }} />
    </div>
  );
}
